import { INTERRUPTED_ERROR } from '@crowdex/agent-contracts';
import { cn } from '@crowdex/ui/lib/utils';
import { CircleSlash, MessageCircleQuestion, XCircle } from 'lucide-react';
import { useContext } from 'react';
import { InterruptedActionContext } from '@/lib/conversations/message-part/interrupted-action-context';
import { questionAnswer } from '@/lib/conversations/message-part/question-answer';
import { stringValue } from '@/lib/conversations/message-part/text';
import { questionItems } from '@/lib/conversations/message-part/tool-identity';
import type { DynamicToolPart } from '@/lib/conversations/message-part/types';
import { TRANSCRIPT_CARD } from './command-card';

function optionLabels(question: Record<string, unknown>): string[] {
  const options = Array.isArray(question['options']) ? question['options'] : [];
  return options.flatMap((option) => {
    if (typeof option !== 'object' || option === null) return [];
    const label = stringValue((option as Record<string, unknown>)['label']);
    return label.length === 0 ? [] : [label];
  });
}

/**
 * A question of the agent's in the transcript. While the agent waits on it, it is docked above
 * the composer (design D13) and nothing shows here; answered, it is one quiet card: the question
 * and the answer. An interrupted question takes its answer from its choices or the composer.
 */
export function QuestionToolCard({ part }: { part: DynamicToolPart }) {
  const questions = questionItems(part);
  const running = part.state === 'input-streaming' || part.state === 'input-available';
  const errorText = part.state === 'output-error' ? part.errorText : undefined;
  const interrupted = errorText === INTERRUPTED_ERROR;
  const failed = part.state === 'output-error' && !interrupted;
  const resume = useContext(InterruptedActionContext);
  const canAnswer = interrupted && resume !== null && resume.activeToolCallId === part.toolCallId;
  const answer = questionAnswer(part);
  // The dock above the composer asks it.
  if (running && resume !== null) return null;
  const single = questions.length === 1 ? questions[0] : undefined;
  const choices = canAnswer && single !== undefined ? optionLabels(single) : [];
  const Icon = interrupted ? CircleSlash : failed ? XCircle : MessageCircleQuestion;

  return (
    <section className={cn(TRANSCRIPT_CARD, 'px-3.5 py-3')} data-question-card>
      <p className="type-meta flex items-center gap-1.5 text-muted-foreground">
        <Icon aria-hidden className={cn('size-3.5', failed && 'text-destructive')} />
        {interrupted
          ? 'Question interrupted'
          : failed
            ? 'Question failed'
            : running
              ? 'Question'
              : 'Question answered'}
      </p>
      {questions.map((question, index) => (
        <p
          className="mt-1 whitespace-pre-wrap text-foreground/90 text-sm"
          // biome-ignore lint/suspicious/noArrayIndexKey: A tool call's questions have no id; their order is their identity.
          key={`${part.toolCallId}-${index}`}
        >
          {stringValue(question['question']) || stringValue(question['prompt'])}
        </p>
      ))}
      {answer !== null && (
        <div className="mt-2 rounded-xl bg-control/60 px-3 py-2">
          <span className="type-meta block text-muted-foreground">Your answer</span>
          <span className="mt-0.5 block whitespace-pre-wrap text-foreground text-sm">{answer}</span>
        </div>
      )}
      {choices.length > 0 && resume !== null && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {choices.map((label) => (
            <button
              className="rounded-control border border-hairline px-3 py-1 text-foreground text-xs transition-colors hover:bg-control"
              key={label}
              onClick={() => resume.onAnswer(part.toolCallId, label)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {canAnswer && (
        <p className="type-meta mt-2 text-muted-foreground">
          Pick a choice or answer in the composer to pick up where it stopped.
        </p>
      )}
      {failed && <p className="mt-2 text-destructive text-xs">{part.errorText}</p>}
    </section>
  );
}
